import { useTheme } from '@automata/mui-theme';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import { IconButton } from '@mui/material';
import { ScaleBand } from 'd3-scale';
import { useState } from 'react';

import { Text } from '../../components/Text';
import { LabwareMenu, MenuOptions } from '../LabwareMenu';

export type Value = {
  id: string;
  label: string;
};

export type Props = {
  values: Value[];
  scaleY: ScaleBand<string>;
  width: number;
  menuOptions?: MenuOptions;
};

const clamp = (input: number, min: number, max: number): number =>
  Math.min(Math.max(input, min), max);

export const AxisY = ({
  values,
  scaleY,
  width,
  menuOptions,
}: Props): JSX.Element => {
  const theme = useTheme();
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [openMenuId, setOpenMenuId] = useState<string | null>(null);

  const handleMenuOpen = (event: React.MouseEvent<HTMLElement>, id: string) => {
    setAnchorEl(event.currentTarget);
    setOpenMenuId(id);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
    setOpenMenuId(null);
  };

  const padding = 10;

  const rowHeight = scaleY.bandwidth();
  const heightThreshold = 10;
  const isShortRow = rowHeight < heightThreshold;

  const fontSizeMin = 8;
  const fontSizeMax = 16;
  const fontSizeTarget = rowHeight * 0.5;
  const fontSizeClamped = clamp(fontSizeTarget, fontSizeMin, fontSizeMax);
  const fontSize = isShortRow ? fontSizeMin : fontSizeClamped;

  const [scaleYRangeMin, scaleYRangeMax] = scaleY.range();

  return (
    <>
      <g id="axis-y">
        <rect
          x={0}
          y={scaleYRangeMin}
          width={Math.max(width, 0)}
          height={Math.max(scaleYRangeMax - scaleYRangeMin, 0)}
          fill={theme.palette.grey[600]}
          opacity={0.1}
        />

        {values.map((value) => {
          const content = isShortRow ? '...' : value.label;
          const baseYPos = (scaleY(value.id) ?? 0) + rowHeight / 2;
          const y = isShortRow ? baseYPos - fontSize / 4 : baseYPos;
          const rowY = scaleY(value.id) ?? 0;
          const buttonSize = Math.min(rowHeight * 0.8, 32);
          const hasMenu = menuOptions && menuOptions.menuActions.length > 0;

          return (
            <g key={value.id}>
              <Text
                x={padding}
                y={y}
                textAnchor="start"
                alignmentBaseline="middle"
                fontWeight={500}
                maxHeight={rowHeight - padding}
                fontSize={fontSize}
                maxWidth={width - padding * 2 - (hasMenu ? buttonSize + 5 : 10)}
              >
                {content}
              </Text>
              {hasMenu && (
                <foreignObject
                  x={width - buttonSize - padding}
                  y={rowY + (rowHeight - buttonSize) / 2}
                  width={buttonSize}
                  height={buttonSize}
                >
                  <IconButton
                    size="small"
                    onClick={(e) => handleMenuOpen(e, value.id)}
                    sx={{
                      padding: 0,
                      width: buttonSize,
                      height: buttonSize,
                      '&:hover': {
                        backgroundColor: 'rgba(0, 0, 0, 0.04)',
                      },
                    }}
                  >
                    <MoreVertIcon sx={{ fontSize: buttonSize * 0.6 }} />
                  </IconButton>
                </foreignObject>
              )}
            </g>
          );
        })}
      </g>
      {menuOptions && (
        <LabwareMenu
          anchorEl={anchorEl}
          labwareId={openMenuId}
          menuOptions={menuOptions}
          onClose={handleMenuClose}
        />
      )}
    </>
  );
};
